import { createComponentStore } from '../../utils/createComponentStore';
import { makeDefaultField } from '../pages/forms/fieldPreviews/fieldTypes';
import { normalizeFormCore, stripField } from '../pages/forms/fieldPreviews/normalizeFieldDefinitions';

const emptyData = () => ({
  // FormCore keys
  fields: [],
  title: '',
  description: '',
  accessPolicy: { mode: 'AUTHENTICATED_SELECTED_USERS', selectedUserIds: [] },
  responseMode: 'FILL_SUBMIT',
  formSettings: {},
  // ReviewTaskOverlay keys
  objective: '',
  taskConfig: {},
  // Flow-membership keys (shared across all v2 form-likes)
  stepperTitle: '',
  flowEntryId: null,
  flowConfig: null,
});

const { getStore: getDynamicReviewTaskStore, defaultStore } = createComponentStore({
  type: 'dynamic_review_task',
  endpoint: 'dynamic-review-task',
  registryKey: 'dynamic_review_task',
  initData: emptyData,
  onLoadData: (rawData) => ({
    ...emptyData(),
    ...normalizeFormCore(rawData),
    // Overlay keys
    objective: rawData.objective || '',
    taskConfig: rawData.taskConfig || {},
    // Flow-membership keys
    stepperTitle: rawData?.stepperTitle || '',
    flowEntryId: rawData?.flowEntryId ?? null,
    flowConfig: rawData?.flowConfig ?? null,
  }),
  extraActions: (set, get) => ({
    // FormCore actions
    setFields: (fields) => set({
      data: {
        ...get().data,
        fields: Array.isArray(fields) ? fields.map(stripField).filter(Boolean) : [],
      },
    }),
    setTitle: (title) => set({ data: { ...get().data, title } }),
    setDescription: (description) => set({ data: { ...get().data, description } }),
    setAccessPolicy: (accessPolicy) => set({ data: { ...get().data, accessPolicy } }),
    setResponseMode: (responseMode) => set({ data: { ...get().data, responseMode } }),
    setFormSettings: (formSettings) => set({ data: { ...get().data, formSettings: { ...get().data.formSettings, ...formSettings } } }),
    addField: () => {
      const fields = [...get().data.fields];
      const order = fields.length;
      fields.push(makeDefaultField('text', order));
      set({ data: { ...get().data, fields } });
    },
    updateField: (index, updates) => {
      const fields = [...get().data.fields];
      fields[index] = stripField({ ...fields[index], ...updates }) || fields[index];
      set({ data: { ...get().data, fields } });
    },
    removeField: (index) => {
      const fields = get().data.fields.filter((_, i) => i !== index);
      set({ data: { ...get().data, fields } });
    },
    reorderFields: (fromIndex, toIndex) => {
      const fields = [...get().data.fields];
      const [moved] = fields.splice(fromIndex, 1);
      fields.splice(toIndex, 0, moved);
      fields.forEach((f, i) => { f.order = i; });
      set({ data: { ...get().data, fields } });
    },
    // ReviewTaskOverlay actions
    setObjective: (objective) => set({ data: { ...get().data, objective } }),
    setTaskConfig: (taskConfig) => set({ data: { ...get().data, taskConfig: { ...get().data.taskConfig, ...taskConfig } } }),
    // Flow-membership actions
    setStepperTitle: (stepperTitle) => set({ data: { ...get().data, stepperTitle } }),
    setFlowSettings: ({ flowEntryId, flowConfig }) => set({
      data: { ...get().data, flowEntryId: flowEntryId ?? null, flowConfig: flowConfig ?? null },
    }),
  }),
});

export { getDynamicReviewTaskStore };
export default defaultStore;
